import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Field } from '../ui/Field'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'

/**
 * Importul unei liste de persoane în evidența de contacte.
 *
 * Ecranul spune deschis ce NU face: nu aduce acord de marketing. Baza care se importă e „un soi
 * amestecat" — audiențe strânse pe Meta, unele cu acord, altele fără, și nimeni nu mai știe care
 * e care. Un import care ar bifa acordul ar da tuturor aparența unuia real, iar de acolo până la
 * un newsletter trimis greșit e o singură apăsare.
 *
 * Sursa lotului e obligatorie fiindcă ea e singurul lucru care face separarea posibilă mai
 * târziu, când se decide ce se trimite și cui.
 */

interface Raport {
  citite: number
  adaugate: number
  actualizate: number
  sarite: number
  erori: { rand: number; mesaj: string }[]
  /** Antetele citite din fisier. Se arata cand n-a intrat nimeni — atunci conteaza. */
  coloane?: string[]
}

function mesaj(e: unknown): string {
  const err = e as {
    response?: { data?: { error?: { message?: string; fields?: Record<string, string[]> } } }
  }
  const eroare = err.response?.data?.error
  return Object.values(eroare?.fields ?? {})[0]?.[0] ?? eroare?.message ?? 'Importul a eșuat'
}

export function ModalImportContacte({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const [sursa, setSursa] = useState('')
  const [fisier, setFisier] = useState<File | null>(null)
  const [raport, setRaport] = useState<Raport | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const importa = useMutation({
    mutationFn: async () => {
      const fd = new FormData()
      fd.append('file', fisier as File)
      fd.append('sursa', sursa.trim())
      const r = await api.post<Raport>('/marketing/sampling/contacte/import', fd, {
        timeout: 180_000,
      })
      return r.data
    },
    onSuccess: (r) => {
      setRaport(r)
      toast.success(`${r.adaugate} adăugate, ${r.actualizate} actualizate`)
      void queryClient.invalidateQueries({ queryKey: ['sampling'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  return (
    <Modal open onClose={onClose} size="lg" title="Importă contacte">
      {raport ? (
        <div className="space-y-3">
          <div className="rounded-lg bg-b4l-green-light p-3 text-sm text-b4l-green-dark">
            <p className="font-medium">Import terminat</p>
            <p className="mt-1">
              {raport.citite} rânduri citite · {raport.adaugate} persoane noi · {raport.actualizate}{' '}
              actualizate · {raport.sarite} sărite
            </p>
          </div>

          {/*
            Cand n-a intrat NIMENI, problema e aproape mereu in antete, nu in date. Aratam ce am
            citit din fisier, ca omul sa vada cu ochii lui de ce n-am gasit emailul — pe 26.08 un
            import de 863 de randuri a esuat integral si raportul repeta acelasi mesaj de 863 de
            ori, fara sa spuna niciodata ce coloane avea fisierul.
          */}
          {raport.adaugate === 0 && raport.actualizate === 0 && raport.citite > 0 && (
            <div className="rounded-lg bg-b4l-amber-light p-3 text-sm text-b4l-amber-dark">
              <p className="font-medium">N-a intrat nimeni. Verifică numele coloanelor.</p>
              <p className="mt-1">
                Fișierul are nevoie de o coloană de email <em>sau</em> una de telefon. Le
                recunoaștem după nume — <code>email</code>, <code>mail</code>, <code>telefon</code>,{' '}
                <code>phone</code>, <code>mobil</code> — sau după orice antet care le conține.
              </p>
              {raport.coloane && raport.coloane.length > 0 && (
                <p className="mt-2">
                  Coloanele găsite în fișier:{' '}
                  <span className="font-mono text-xs">{raport.coloane.join(', ')}</span>
                </p>
              )}
            </div>
          )}

          {raport.erori.length > 0 && (
            <div>
              <p className="text-sm font-medium text-b4l-text">Rânduri sărite</p>
              <div className="mt-1 max-h-56 overflow-y-auto rounded-lg border border-b4l-border">
                {raport.erori.map((e) => (
                  <div
                    key={e.rand}
                    className="border-b border-b4l-border px-3 py-1.5 text-xs text-b4l-text-muted last:border-0"
                  >
                    Rândul {e.rand}: {e.mesaj}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/*
            Sursa e a lotului, nu a omului: cine exista deja in baza si-o pastreaza pe cea de
            dinainte. Altfel textul trimitea la un filtru gol — 27 de persoane „actualizate" nu
            se gaseau nicaieri sub eticheta tocmai introdusa (02.09.2026).
          */}
          <p className="text-xs text-b4l-text-muted">
            Niciuna dintre persoanele importate n-a primit acord de marketing.
            {raport.adaugate > 0 && (
              <>
                {' '}
                Cele {raport.adaugate} persoane noi le găsești filtrând după sursa „{sursa.trim()}”.
              </>
            )}
            {raport.actualizate > 0 && (
              <>
                {' '}
                Cele {raport.actualizate} care existau deja au fost completate cu datele din fișier,
                dar își păstrează sursa de dinainte — nu apar sub „{sursa.trim()}”.
              </>
            )}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="rounded-lg bg-b4l-amber-light p-3 text-sm text-b4l-amber-dark">
            <strong>Importul nu aduce acord de marketing.</strong> Persoanele intră în bază, dar nu
            apar la filtrul „cei cu acord” și nu primesc newslettere. Un acord pe care nu-l poți
            dovedi e ca și cum n-ar exista — iar dacă ar apărea bifat, ar ajunge într-un mesaj
            trimis din greșeală.
          </div>

          <Field
            label="Sursa lotului"
            value={sursa}
            onChange={(e) => setSursa(e.target.value)}
            placeholder="Ex.: audiență Meta — campania toamnă 2026"
            hint="Obligatorie. E singurul lucru după care se pot separa loturile mai târziu."
          />

          <label className="block">
            <span className="text-sm font-medium text-b4l-text">Fișier</span>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => setFisier(e.target.files?.[0] ?? null)}
              className="mt-1 w-full rounded-lg border border-b4l-border p-2 text-sm"
            />
            <span className="mt-1 block text-xs text-b4l-text-muted">
              XLSX sau CSV. Coloane recunoscute: <code>nume</code>, <code>prenume</code> (sau{' '}
              <code>nume_complet</code>), <code>email</code>, <code>telefon</code>,{' '}
              <code>ocupatie</code>. Un rând fără email și fără telefon se sare — n-am pe cine
              recunoaște și nici cui scrie.
            </span>
          </label>
        </div>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          {raport ? 'Închide' : 'Renunță'}
        </Button>
        {!raport && (
          <Button
            disabled={!fisier || sursa.trim().length < 3 || importa.isPending}
            onClick={() => importa.mutate()}
          >
            {importa.isPending ? 'Se importă…' : 'Importă'}
          </Button>
        )}
      </div>
    </Modal>
  )
}
